import {
  Home,
  Landmark,
  MessageCircle,
  Handshake,
  Target,
  CalendarCheck,
  Ban,
  HeartHandshake,
  HousePlus,
  CheckCircle2,
  UserCog,
  Zap,
} from "lucide-react";
import { useEffect } from "react";
import { useResource } from "@/hooks/useResource";
import { clientSnapshotService } from "@/services/clientSnapshotService";
import { useSettingsStore } from "@/store/settingsStore";
import { useUiStore } from "@/store/uiStore";
import { PageHeader } from "@/components/molecules/PageHeader";
import { StatCard } from "@/components/molecules/StatCard";
import type { ClientSnapshot } from "@/types/entities";

const numberFormatter = new Intl.NumberFormat("en-GB");
const pct = (value: number) => `${value.toFixed(1)}%`;

function formatDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return "Not captured";
  if (seconds < 60) return `${Math.round(seconds)} seconds`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
  return `${(seconds / 3600).toFixed(1)} hours`;
}

const TILES: Array<{
  key: string;
  label: string;
  icon: JSX.Element;
  value: (s: ClientSnapshot) => string;
}> = [
  { key: "valuations", label: "Valuation opportunities identified", icon: <Home size={18} />, value: (s) => numberFormatter.format(s.valuations) },
  { key: "mortgage", label: "Mortgage referrals generated", icon: <Landmark size={18} />, value: (s) => numberFormatter.format(s.mortgageReferrals) },
  { key: "leadsInteracted", label: "Total leads interacted", icon: <MessageCircle size={18} />, value: (s) => numberFormatter.format(s.leadsInteracted) },
  { key: "engagementRate", label: "Engagement rate", icon: <Handshake size={18} />, value: (s) => `${numberFormatter.format(s.replies)} (${pct(s.engagementRatePct)})` },
  { key: "qualified", label: "Qualified leads", icon: <Target size={18} />, value: (s) => `${numberFormatter.format(s.qualifiedLeads)} (${pct(s.qualifiedRatePct)})` },
  { key: "booked", label: "Total leads booked", icon: <CalendarCheck size={18} />, value: (s) => `${numberFormatter.format(s.totalLeadsBooked)} (${pct(s.bookedRatePct)})` },
  { key: "unqualified", label: "Unqualified leads", icon: <Ban size={18} />, value: (s) => `${numberFormatter.format(s.unqualifiedLeads)} (${pct(s.unqualifiedRatePct)})` },
  { key: "satisfaction", label: "Satisfaction rate", icon: <HeartHandshake size={18} />, value: (s) => `${numberFormatter.format(s.satisfactionCount)} (${pct(s.satisfactionRatePct)})` },
  { key: "propertiesRecommended", label: "Properties recommended", icon: <HousePlus size={18} />, value: (s) => numberFormatter.format(s.propertiesRecommended) },
  { key: "propertiesRecommendedBooked", label: "Properties recommended & booked", icon: <CheckCircle2 size={18} />, value: (s) => numberFormatter.format(s.propertiesRecommendedBooked) },
  { key: "escalations", label: "Escalations to human support", icon: <UserCog size={18} />, value: (s) => `${numberFormatter.format(s.escalations)} (${pct(s.escalationRatePct)})` },
  { key: "responseTime", label: "Average response time", icon: <Zap size={18} />, value: (s) => formatDuration(s.avgResponseSeconds) },
];

export function ClientSnapshotDashboardPage() {
  const { legalEntityId, legalEntityName } = useSettingsStore();
  const { startPageLoading, stopPageLoading } = useUiStore();
  const { data, loading, error } = useResource(
    () => (legalEntityId ? clientSnapshotService.get(legalEntityId) : Promise.resolve(null)),
    [legalEntityId]
  );

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  const anchor = data?.generatedAt ? data.generatedAt.slice(0, 10) : undefined;

  return (
    <div className="h-full overflow-auto">
      <PageHeader
        title="Client Snapshot"
        subtitle={
          legalEntityId
            ? `This week, Bridge AI delivered for ${legalEntityName ?? "the selected organization"}.${anchor ? ` As of ${anchor}.` : ""}`
            : "Select an organization above to see its weekly client snapshot."
        }
      />
      {!legalEntityId ? null : (
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <StatCard
              key={tile.key}
              label={tile.label}
              icon={tile.icon}
              loading={loading}
              value={error ? "—" : data ? tile.value(data) : "—"}
            />
          ))}
        </div>
      )}
    </div>
  );
}
